import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, Mail, Send, Undo2 } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { useCompanyAccess } from '../../Context/CompanyAccessContext';
import { approvalError, askForApproval, decideApproval, getApprovalState } from './offerApprovalsApi';

/**
 * Where this candidate's draft stands, refreshed while the review step is open.
 * undefined until the server answers; null if it can't, which a server without
 * approvals does, and then the offer releases as it always has.
 */
export function useOfferApproval(applicationId, active) {
  const [approval, setApproval] = useState(undefined);
  const refresh = useCallback(async () => {
    if (!applicationId) return;
    try {
      setApproval(await getApprovalState(applicationId));
    } catch (err) {
      setApproval(null);
    }
  }, [applicationId]);
  useEffect(() => {
    if (active) refresh();
    else setApproval(undefined);
  }, [active, refresh]);
  return [approval, refresh];
}

const noteFor = (approval) => {
  switch (approval?.status) {
    case 'pending':
      return `Waiting for approval. Asked by ${approval.requested_by}.`;
    case 'approved':
      return `Approved by ${approval.decided_by}.`;
    case 'rejected':
      return `Sent back by ${approval.decided_by}${approval.comment ? `: "${approval.comment}"` : '.'}`;
    case 'changed':
      return 'The offer changed after approval was asked for. Ask again.';
    default:
      return 'This offer needs approval before it is sent.';
  }
};

/** True once this draft may be sent: approvals are off, or this exact draft is approved. */
export const approvalAllowsRelease = (approval) => !approval?.required || approval.status === 'approved';

const NOTE_TONE = {
  approved: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  rejected: 'border-amber-200 bg-amber-50 text-amber-900',
};

/** One line above the review step's buttons saying where approval stands. */
export function OfferApprovalNote({ approval }) {
  if (!approval?.required) return null;
  const tone = NOTE_TONE[approval.status] || 'border-slate-200 bg-slate-50 text-slate-700';
  return <p role="status" className={`rounded-md border px-3 py-2 text-sm ${tone}`}>{noteFor(approval)}</p>;
}

/**
 * The last buttons of the offer review step. Without the company's approval switch,
 * or once this exact draft is approved, it is the Release button. Otherwise it is
 * the one action that fits: ask for approval, wait, or approve and send back.
 */
export function OfferReleaseActions({
  approval, onApprovalChanged, applicationId, draftReady, busy, offerLocked, releasing, onRelease,
}) {
  const { can } = useCompanyAccess();
  const [working, setWorking] = useState('');
  const [sendingBack, setSendingBack] = useState(false);
  const [reason, setReason] = useState('');
  const needsApproval = Boolean(approval?.required) && approval?.status !== 'approved';
  const disabled = !draftReady || busy || Boolean(working) || approval === undefined;

  const run = async (label, action, done) => {
    setWorking(label);
    try {
      await action();
      toast.success(done);
      setSendingBack(false);
      setReason('');
      await onApprovalChanged();
    } catch (err) {
      toast.error(approvalError(err, 'That did not work. Please try again.'));
    } finally {
      setWorking('');
    }
  };

  if (!needsApproval) {
    if (!can('offers.release')) return null;
    return (
      <Button type="button" onClick={onRelease} disabled={disabled || offerLocked}>
        {releasing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
        {releasing ? 'Sending...' : 'Release Offer'}
      </Button>
    );
  }

  if (approval.status === 'pending' && approval.can_decide) {
    if (sendingBack) {
      return (
        <>
          <Input
            aria-label="What needs to change"
            placeholder="What needs to change?"
            className="h-9 w-56"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button type="button" variant="outline" onClick={() => setSendingBack(false)} disabled={Boolean(working)}>
            Back
          </Button>
          <Button
            type="button"
            onClick={() => run('reject', () => decideApproval(approval.approval_id, false, reason), 'Offer sent back.')}
            disabled={!reason.trim() || Boolean(working)}
          >
            {working === 'reject' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
            Send back
          </Button>
        </>
      );
    }
    return (
      <>
        <Button type="button" variant="outline" onClick={() => setSendingBack(true)} disabled={disabled}>
          <Undo2 className="mr-2 h-4 w-4" />
          Send back
        </Button>
        <Button
          type="button"
          onClick={() => run('approve', () => decideApproval(approval.approval_id, true), 'Offer approved.')}
          disabled={disabled}
        >
          {working === 'approve' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Approve
        </Button>
      </>
    );
  }

  if (approval.status === 'pending') {
    return <Button type="button" disabled>Waiting for approval</Button>;
  }

  if (!can('offers.draft')) return null;
  return (
    <>
      <Button
        type="button"
        onClick={() => run('ask', () => askForApproval(applicationId), 'Sent for approval.')}
        disabled={disabled}
      >
        {working === 'ask' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
        {approval.status === 'none' ? 'Ask for approval' : 'Ask for approval again'}
      </Button>
    </>
  );
}
